import type { TablePaginationConfig } from 'antd/es/table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

const DEFAULT_PAGE_SIZE = 10

const renderShowTotal = (total: number, range: [number, number]) => (
  // tabular-nums keeps digit widths uniform so the counter
  // doesn't shift horizontally as numbers change.
  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
    {range[0]} - {range[1]} of {total}
  </span>
)

export type UseTablePaginationResult = {
  paginationProps: TablePaginationConfig
  resetToFirstPage: () => void
}

export function useTablePagination(
  total: number,
  defaultPageSize: number = DEFAULT_PAGE_SIZE
): UseTablePaginationResult {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  const resetToFirstPage = useCallback(() => {
    setPage(1)
  }, [])

  // When total shrinks (e.g. delete last row on final page), clamp page — AntD
  // controlled pagination does not emit onChange to correct an out-of-range current.
  useEffect(() => {
    setPage((curr) => {
      const maxPage = Math.max(1, Math.ceil(total / pageSize))
      return curr > maxPage ? maxPage : curr
    })
  }, [total, pageSize])

  const onChange = useCallback(
    (nextPage: number, nextPageSize: number) => {
      if (nextPageSize !== pageSize) {
        // Reset to page 1 so the user doesn't land on a now-empty
        // page when increasing the page size.
        setPageSize(nextPageSize)
        setPage(1)
      } else {
        setPage(nextPage)
      }
    },
    [pageSize]
  )

  const paginationProps = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      showTotal: renderShowTotal,
      onChange,
    }),
    [page, pageSize, total, onChange]
  )

  return { paginationProps, resetToFirstPage }
}
